import { SUBJECTS } from '@jeraisonne/db';
import type { SchoolLevel, SubjectSlug } from '@jeraisonne/db';
import { cn } from '@jeraisonne/ui';

interface SubjectsMultiselectProps {
  value: SubjectSlug[];
  onChange: (value: SubjectSlug[]) => void;
  /** Restreint les options à ce niveau scolaire. Absent = toutes les SUBJECTS. */
  levelFilter?: SchoolLevel | null;
}

/**
 * Grille de cases à cocher pour choisir 0..N matières (vide = « toutes
 * matières » côté appelant — cf. `student_profiles.subjects`,
 * `tutor_links.subjects`). Composant présentational pur, factorisé depuis
 * `StudentSubjectsControl` et l'étape « Tes matières » de l'onboarding pour
 * éviter de dupliquer la même grille sur les formulaires d'invitation/demande
 * de tuteur et les contrôles d'édition post-création (migration 0227).
 */
export function SubjectsMultiselect({ value, onChange, levelFilter }: SubjectsMultiselectProps) {
  const options = SUBJECTS.filter((s) => !levelFilter || s.levels.includes(levelFilter));

  function toggle(subject: SubjectSlug) {
    onChange(value.includes(subject) ? value.filter((s) => s !== subject) : [...value, subject]);
  }

  return (
    <div className="grid grid-cols-2 gap-2">
      {options.map((s) => {
        const isSelected = value.includes(s.value);
        return (
          <label
            key={s.value}
            className={cn(
              'flex min-h-[2.75rem] cursor-pointer items-center gap-2 rounded-xl border-2 p-3 text-sm font-semibold transition-all active:scale-[0.98]',
              isSelected
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-ink-100 text-ink-700 hover:border-primary-300 bg-white',
            )}
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => toggle(s.value)}
              className="sr-only"
            />
            <span aria-hidden="true">{s.emoji}</span>
            <span>{s.label}</span>
          </label>
        );
      })}
    </div>
  );
}
